import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import AuthService from 'utils/AuthService';
import {
  PanelRootContext,
  PhoneType,
  Property,
  PropertyGroup,
  Role,
  Status,
  Tenant,
  TraxeroActionResponse,
  User,
  UserData,
} from 'types';
import { capitalize, getFormattedUserName } from 'utils';
import { Grid } from '@mui/material';
import { Notifications } from 'components/inputs/NotificationPrefs';
import RolesBox from 'components/inputs/RoleSelect';
import {
  useNavigate,
  useOutletContext,
  useParams,
  useRouteLoaderData,
  useLoaderData,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import { getHandlerForDisplayMode } from './UsersPanel';
import { useFetcher } from 'react-router';
import { getUser } from 'api/users';
import { AppContext } from 'context/appContext';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import DetailsHeader from 'components/layout/DetailsHeader';
import FormFrame from 'components/layout/FormFrame';
import { ValidatorForm } from 'react-material-ui-form-validator';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import {
  getActionRouteForDisplayMode,
  PeopleTabLayoutLoaderData,
  getLoaderRouteForDisplayMode,
  PeopleTabActionHandler,
} from './PeopleTabLayout';
import { getBlankUser } from 'utils';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { ROUTES, routeWithParams } from 'router/routes';
import { ToastContext, TOAST_TYPE } from 'context/toastContext';

interface UserFormLoaderParams {
  params: {
    userID?: string;
  };
}

export interface UserFormLoaderData {
  _user: Promise<User> | null;
}

export const userFormLoader = ({
  params,
}: UserFormLoaderParams): UserFormLoaderData => {
  const userID = Number(params.userID);
  if (userID > 0) {
    return { _user: getUser(userID) };
  }
  return { _user: null };
};

export const UserForm = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const { userID } = useParams();
  const displayToast = useContext(ToastContext);
  const { displayMode, entity } =
    useOutletContext<PanelRootContext<Tenant | Property | PropertyGroup>>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User>(getBlankUser(state.tenant.id));
  const formMode = userID === '0' ? 'create' : 'edit';
  const { _user } = useLoaderData() as UserFormLoaderData;
  const { _users } = useRouteLoaderData(
    getLoaderRouteForDisplayMode(displayMode),
  ) as PeopleTabLayoutLoaderData;
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });

  const hasMobilePhone =
    user?.contact?.primaryPhoneType === PhoneType.Mobile ||
    user?.contact?.secondaryPhoneType === PhoneType.Mobile ||
    user?.contact?.tertiaryPhoneType === PhoneType.Mobile;

  useEffect(() => {
    if (!hasMobilePhone) {
      setUser((prevUser) => ({
        ...prevUser,
        userData: {
          ...prevUser.userData,
          notifications: {
            ...prevUser.userData.notifications,
            notificationType: {
              ...prevUser.userData.notifications.notificationType,
              push: false,
            },
          },
        },
      }));
    }
  }, [hasMobilePhone]);

  useEffect(() => {
    const loadUser = async () => {
      if (_user) {
        const loadedUser = await _user;
        if (loadedUser) {
          setUser(loadedUser);
        }
      }
    };
    loadUser();
  }, [_user]);

  const getSuccessMessage = (
    res: TraxeroActionResponse<PeopleTabActionHandler>,
  ): string => {
    const userLabel = t('user.label', { ns: 'entities' });
    const propertyLabel = t('property.label', { ns: 'entities' });
    const propertyGroupLabel = t('property_group.label', { ns: 'entities' });

    switch (res.handler) {
      case PeopleTabActionHandler.TENANT_USER_CREATE:
      case PeopleTabActionHandler.PROPERTY_USER_CREATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_CREATE:
        return t('create_success', {
          name: `${userLabel} ${getFormattedUserName(user)}`,
          ns: 'toasts',
        });
      case PeopleTabActionHandler.TENANT_USER_UPDATE:
      case PeopleTabActionHandler.PROPERTY_USER_UPDATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPDATE:
        return t('update_success', {
          name: userLabel,
          ns: 'toasts',
        });
      case PeopleTabActionHandler.PROPERTY_USER_UPSERT:
        return t('update_success', {
          name: propertyLabel,
          ns: 'toasts',
        });
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPSERT:
        return t('update_success', {
          name: propertyGroupLabel,
          ns: 'toasts',
        });
      default:
        return '';
    }
  };

  const getErrorMessage = (
    res: TraxeroActionResponse<PeopleTabActionHandler>,
  ): string => {
    const { handler, status, statusText } = res;
    const errorSuffix = `${status} - ${statusText}`;
    const userLabel = t('user.label', { ns: 'entities' });
    const userLabelPlural = t('user.label_plural', { ns: 'entities' });
    const propertyLabel = t('property.label', { ns: 'entities' });
    const propertyGroupLabel = t('property_group.label', { ns: 'entities' });

    switch (handler) {
      case PeopleTabActionHandler.TENANT_USER_UPDATE:
      case PeopleTabActionHandler.PROPERTY_USER_UPDATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPDATE:
        return t('update_error', {
          name: userLabel,
          error: errorSuffix,
          ns: 'toasts',
        });
      case PeopleTabActionHandler.TENANT_USER_CREATE:
      case PeopleTabActionHandler.PROPERTY_USER_CREATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_CREATE:
        if (statusText.toLowerCase().includes('user already exists')) {
          return t('user_already_exists', { ns: 'toasts' });
        }
        return t('create_error', {
          name: userLabel,
          error: errorSuffix,
          ns: 'toasts',
        });
      case PeopleTabActionHandler.PROPERTY_USER_UPSERT:
        return t('update_error', {
          name: `${propertyLabel} ${userLabelPlural}`,
          error: errorSuffix,
          ns: 'toasts',
        });
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPSERT:
        return t('update_error', {
          name: `${propertyGroupLabel} ${userLabelPlural}`,
          error: errorSuffix,
          ns: 'toasts',
        });
      default:
        return t('unknown_error', { ns: 'toasts' });
    }
  };

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PeopleTabActionHandler>;
      if (response.status < 400) {
        navigate(-1);
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher.data]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      switch (name) {
        case 'firstName':
          setUser((prevUser) => ({
            ...prevUser,
            firstName: value,
          }));
          break;
        case 'lastName':
          setUser((prevUser) => ({ ...prevUser, lastName: value }));
          break;
        case 'email':
          setUser((prevUser) => ({ ...prevUser, email: value }));
          break;
        case 'externalID':
          setUser((prevUser) => ({ ...prevUser, externalID: value }));
          break;
        case 'contact.description':
          setUser((prevUser) => ({
            ...prevUser,
            contact: {
              ...prevUser.contact,
              id: prevUser.contact?.id || 0,
              name: prevUser.contact?.name || '',
              status: prevUser.contact?.status || Status.Active,
              tenantID: prevUser.contact?.tenantID || 0,
              description: value,
              location: {
                ...prevUser.contact?.location,
              },
            },
          }));
          break;
        case 'contact.secondaryEmail':
          setUser((prevUser) => ({
            ...prevUser,
            contact: {
              ...prevUser.contact,
              id: prevUser.contact?.id || 0,
              name: prevUser.contact?.name || '',
              status: prevUser.contact?.status || Status.Active,
              tenantID: prevUser.contact?.tenantID || 0,
              secondaryEmail: value,
              location: {
                ...prevUser.contact?.location,
              },
            },
          }));
          break;
        default:
          break;
      }
    },
    [user],
  );

  const handleSubmit = async () => {
    
    // set contact fields based on user fields
    const submittedUser: User = { ...user };
    submittedUser.contact = {
      ...submittedUser.contact,
      id: submittedUser?.contact?.id || 0,
      status: Status.Active,
      name: getFormattedUserName(submittedUser),
      primaryEmail: submittedUser.email,
      tenantID: state.tenant.id,
      location: {
        ...submittedUser.contact?.location,
      },
    };

    if (formMode === 'edit') {
      fetcher.submit(
        {
          handler: getHandlerForDisplayMode(displayMode, formMode),
          body: JSON.stringify(submittedUser),
          tenantID: state.tenant.id,
          entityID: entity?.id,
          userID: user.id,
        },
        {
          method: 'PUT',
          action: getActionRouteForDisplayMode({
            displayMode,
            propertyID: entity?.id,
            groupID: entity?.id,
            tenantID: state.tenant.id,
          }),
          encType: 'application/json',
        },
      );
    } else if (displayMode === 'tenant') {
      // create tenant user
      fetcher.submit(
        {
          handler: getHandlerForDisplayMode(displayMode, formMode),
          body: JSON.stringify(submittedUser),
          userID: user.id,
          tenantID: state.tenant.id,
          entityID: entity?.id,
        },
        {
          method: 'POST',
          action: getActionRouteForDisplayMode({
            displayMode,
            propertyID: entity?.id,
            groupID: entity?.id,
            tenantID: state.tenant.id,
          }),
          encType: 'application/json',
        },
      );
    } else {
      // create property or group user (upsert)
      const users = (await _users) as User[];
      fetcher.submit(
        {
          handler: getHandlerForDisplayMode(displayMode, formMode),
          body: JSON.stringify([...users, submittedUser]),
          tenantID: state.tenant.id,
          entityID: entity?.id,
          userID: user.id,
        },
        {
          method: 'PUT',
          action: getActionRouteForDisplayMode({
            displayMode,
            propertyID: entity?.id,
            groupID: entity?.id,
            tenantID: state.tenant.id,
          }),
          encType: 'application/json',
        },
      );
    }
  };

  const submitButtonText =
    userID === '0'
      ? t('buttons.create', { name: t('user.label', { ns: 'entities' }) })
      : t('buttons.update', { name: t('user.label', { ns: 'entities' }) });

  const handleCancel = () => {
    switch (displayMode) {
      case 'tenant':
        navigate(routeWithParams(ROUTES.USERS, [state.tenant.id]));
        break;
      case 'property':
        navigate(
          routeWithParams(ROUTES.PROPERTY_USERS, [state.tenant.id, entity.id]),
        );
        break;
      case 'group':
        navigate(
          routeWithParams(ROUTES.PROPERTY_GROUP_USERS, [
            state.tenant.id,
            entity.id,
          ]),
        );
        break;
    }
  };

  const canModifyRoles = useMemo(() => {
    if (user.status === Status.External) {
      return false;
    }
    if (AuthService.IsSuperAdmin()) {
      return true;
    }
    return !!state.entityPermissions.user.update;
  }, [state.entityPermissions.user.update, user.status]);

  const preventContactEditsForUser = useMemo(() => {
    if (formMode === 'edit') {
      return user.status === Status.Pending || user.status === Status.External;
    }
    return false;
  }, [formMode, user.status]);

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={
            getFormattedUserName(user) ||
            t('buttons.create', { name: t('user.label', { ns: 'entities' }) })
          }
          submitLabel={submitButtonText}
          onCancel={handleCancel}
          readonly={!state.entityPermissions.user.update}
          disableSubmit={fetcher.state !== 'idle'}
        />
        <ScrollableFormContainer maxHeight='calc(100vh - 326px)'>
          <SettingsSection
            title={`${t('user.label', { ns: 'entities' })} ${t('details')}`}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='firstName'
                  label={t('user.firstName', { ns: 'entities' })}
                  value={capitalize(user.firstName)}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='lastName'
                  label={t('user.lastName', { ns: 'entities' })}
                  value={capitalize(user.lastName)}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='externalID'
                  label={t('user.externalID', { ns: 'entities' })}
                  value={user?.externalID || ''}
                  onChange={handleChange}
                  readonly={!AuthService.IsSuperAdmin()}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  name={'status'}
                  label={t('user.status', { ns: 'entities' })}
                  value={user.status ?? Status.Pending}
                  setValue={(e: ChangeEvent<HTMLInputElement>) =>
                    setUser((prevUser) => ({
                      ...prevUser,
                      status: e.target.value as Status,
                    }))
                  }
                  disabled={user.status === Status.Pending || formMode === 'create' || user.status === Status.External}
                  options={[Status.Active, Status.Disabled, Status.Pending, Status.External]}
                  readonly={!AuthService.IsSuperAdmin()}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('user.contact.label', { ns: 'entities' })}
            subtitle={
              preventContactEditsForUser
                ? t('cannot_update_pending_user')
                : ''
            }
            subtitleError={preventContactEditsForUser}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name={'contact.description'}
                  label={t('user.contact.description', { ns: 'entities' })}
                  value={user?.contact?.description || ''}
                  onChange={handleChange}
                  readonly={preventContactEditsForUser}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='email'
                  label={t('user.email', { ns: 'entities' })}
                  value={user?.email || user.contact?.primaryEmail || ''}
                  onChange={handleChange}
                  validators={['isEmail']}
                  errorMessages={['Email is not valid']}
                  required={userID === '0'}
                  readonly={preventContactEditsForUser}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name={'contact.secondaryEmail'}
                  label={t('user.contact.secondaryEmail', { ns: 'entities' })}
                  value={user?.contact?.secondaryEmail || ''}
                  onChange={handleChange}
                  validators={['isEmail']}
                  errorMessages={['Email is not valid']}
                  readonly={preventContactEditsForUser}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  name={'phone'}
                  label={t('user.contact.primaryPhone', { ns: 'entities' })}
                  phoneType={user?.contact?.primaryPhoneType}
                  readonly={preventContactEditsForUser}
                  setPhoneType={(type: PhoneType) => {
                    setUser((prevUser) => ({
                      ...prevUser,
                      contact: {
                        ...prevUser.contact,
                        id: prevUser.contact?.id || 0,
                        name: prevUser.contact?.name || '',
                        status: prevUser.contact?.status || Status.Active,
                        tenantID: prevUser.contact?.tenantID || 0,
                        primaryPhoneType: type,
                        location: { ...prevUser.contact?.location },
                      },
                    }));
                  }}
                  typeFieldLabel={t('user.contact.primaryPhoneType')}
                  typeFieldName='primaryPhoneType'
                  phoneNumber={user?.contact?.primaryPhone || ''}
                  setPhoneNumber={(phone: string) => {
                    setUser((prevUser) => ({
                      ...prevUser,
                      contact: {
                        ...prevUser.contact,
                        id: prevUser.contact?.id || 0,
                        name: prevUser.contact?.name || '',
                        status: prevUser.contact?.status || Status.Active,
                        tenantID: prevUser.contact?.tenantID || 0,
                        primaryPhone: phone,
                        location: { ...prevUser.contact?.location },
                      },
                    }));
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  name={'contact.secondaryPhone'}
                  label={t('user.contact.secondaryPhone', { ns: 'entities' })}
                  phoneType={user?.contact?.secondaryPhoneType}
                  readonly={preventContactEditsForUser}
                  setPhoneType={(type: PhoneType) => {
                    setUser((prevUser) => ({
                      ...prevUser,
                      contact: {
                        ...prevUser.contact,
                        id: prevUser.contact?.id || 0,
                        name: prevUser.contact?.name || '',
                        status: prevUser.contact?.status || Status.Active,
                        tenantID: prevUser.contact?.tenantID || 0,
                        secondaryPhoneType: type,
                        location: { ...prevUser.contact?.location },
                      },
                    }));
                  }}
                  phoneNumber={user?.contact?.secondaryPhone || ''}
                  setPhoneNumber={(phone: string) => {
                    setUser((prevUser) => ({
                      ...prevUser,
                      contact: {
                        ...prevUser.contact,
                        id: prevUser.contact?.id || 0,
                        name: prevUser.contact?.name || '',
                        status: prevUser.contact?.status || Status.Active,
                        tenantID: prevUser.contact?.tenantID || 0,
                        secondaryPhone: phone,
                        location: { ...prevUser.contact?.location },
                      },
                    }));
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  name={'contact.tertiaryPhone'}
                  label={t('user.contact.tertiaryPhone', { ns: 'entities' })}
                  readonly={preventContactEditsForUser}
                  typeFieldName={'contact.tertiaryPhoneType'}
                  typeFieldLabel='Type'
                  phoneType={user?.contact?.tertiaryPhoneType}
                  phoneNumber={user?.contact?.tertiaryPhone || ''}
                  setPhoneNumber={(phone: string) => {
                    setUser((prevUser) => ({
                      ...prevUser,
                      contact: {
                        ...prevUser.contact,
                        id: prevUser.contact?.id || 0,
                        name: prevUser.contact?.name || '',
                        status: prevUser.contact?.status || Status.Active,
                        tenantID: prevUser.contact?.tenantID || 0,
                        tertiaryPhone: phone,
                        location: { ...prevUser.contact?.location },
                      },
                    }));
                  }}
                  setPhoneType={(type: PhoneType) => {
                    setUser((prevUser) => ({
                      ...prevUser,
                      contact: {
                        ...prevUser.contact,
                        id: prevUser.contact?.id || 0,
                        name: prevUser.contact?.name || '',
                        status: prevUser.contact?.status || Status.Active,
                        tenantID: prevUser.contact?.tenantID || 0,
                        tertiaryPhoneType: type,
                        location: { ...prevUser.contact?.location },
                      },
                    }));
                  }}
                />
              </Grid>
              <Grid
                mt={{ xs: 2, lg: 0 }}
                sx={{ display: 'flex', justifyContent: 'flex-start' }}
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <Notifications
                  data={user.userData}
                  setUserData={(userData: UserData) =>
                    setUser((prevUser) => ({ ...prevUser, userData }))
                  }
                  hasEmailAddress={!!user.email}
                  hasMobilePhone={hasMobilePhone}
                  readonly={!state.entityPermissions.user.update}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('user.roles', { ns: 'entities' })}
            divider={false}
            subtitle={user.status === Status.External ? t('cannot_update_external_user_roles') : ''}
            subtitleError={user.status === Status.External}
          >
            <RolesBox
              userRoles={user.roles}
              setUserRoles={(roles: Role[]) =>
                setUser((prevUser) => ({ ...prevUser, roles }))
              }
              readonly={!canModifyRoles}
            />
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};
